import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, Download, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const TEMPLATE_HEADER = 'bundle_barcode,design_name,color_name,color_code,sort_no,vendor_name,fg_name,quantity,product_type,meters,usn_codes';
const TEMPLATE_SAMPLE = 'BUN0001/25-26,Praga 3/4 C 2.50,Cream,CR01,S101,VendorName,FG10001,3,dhoti,,USN-A|USN-B|USN-C';
const TEMPLATE_SAMPLE_RM = 'BUN0002/25-26,Running Fabric,White,WH01,S102,VendorName,FG10002,1,running_mtr,88.5,USN-RM1';

const LegacyBundleBackfill = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(false);

  const downloadTemplate = () => {
    const blob = new Blob([`${TEMPLATE_HEADER}\n${TEMPLATE_SAMPLE}\n${TEMPLATE_SAMPLE_RM}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'legacy_greige_bundle_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setResult(null);
    setApplied(false);
  };

  const run = async (dryRun) => {
    if (!file) {
      toast.error('Please choose a CSV file first');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('dry_run', dryRun ? 'true' : 'false');
      const res = await apiClient.post('/sections/greige/admin/legacy-bundle-backfill', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (dryRun) {
        setApplied(false);
        if (res.data.error_count > 0) {
          toast.error(`Preview found ${res.data.error_count} error(s). Fix the CSV before applying.`);
        } else {
          toast.success(`Preview ready: ${res.data.bundles_parsed} bundle(s), ${res.data.usns_parsed} USN(s)`);
        }
      } else {
        setApplied(true);
        toast.success(`Backfill applied — Audit ${res.data.audit_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBusy(false);
    }
  };

  const canApply = result && !result.blocked && result.error_count === 0 && result.bundles_parsed > 0 && !applied;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/sections/greige')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Greige Section
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Database className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Legacy Bundle Backfill</h1>
            <p className="text-sm text-slate-500">
              Bulk-mark completed legacy greige bundles via CSV — no dummy scans needed.
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Upload CSV</CardTitle>
            <CardDescription>
              One row per bundle. USNs in the <code>usn_codes</code> column separated by <code>|</code> or comma.
              This writes the completed/stored state into <strong>greige_bundles</strong>,{' '}
              <strong>greige_usns</strong> and <strong>greige_storage_inventory</strong> (idempotent).
              For a <strong>running-metre roll</strong> set <code>product_type=running_mtr</code>, give it{' '}
              <strong>exactly one</strong> <code>usn_codes</code> value, and put the decimal metres in the{' '}
              <code>meters</code> column — it is stored as the USN&apos;s <code>qty_mtr</code> and tagged{' '}
              <code>RUNNING_MTR</code> so Processing picks it up correctly. Leave <code>product_type</code> blank
              (or <code>dhoti</code>) for piece bundles.
              It does <strong>not</strong> create transfers — move bundles greige-storage → processing → inhouse as usual.
              Always run <strong>Preview</strong> first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={onPick}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="choose-file-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> {file ? file.name : 'Choose CSV'}
              </Button>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => run(true)} disabled={busy || !file} variant="outline" data-testid="preview-btn">
                <Upload className={`w-4 h-4 mr-2 ${busy ? 'animate-pulse' : ''}`} />
                {busy ? 'Working…' : 'Preview'}
              </Button>
              <Button
                onClick={() => {
                  if (window.confirm(`Apply backfill for ${result?.bundles_parsed} bundle(s) / ${result?.usns_parsed} USN(s)? This upserts the 3 greige collections (idempotent, audited).`)) {
                    run(false);
                  }
                }}
                disabled={busy || !canApply}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="apply-btn"
              >
                Apply Backfill
              </Button>
            </div>
          </CardContent>
        </Card>

        {result && (
          <Card data-testid="backfill-result">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {applied ? (
                  <><CheckCircle className="w-5 h-5 text-emerald-600" /> Backfill Applied</>
                ) : result.error_count > 0 ? (
                  <><AlertTriangle className="w-5 h-5 text-red-600" /> Preview — Errors Found</>
                ) : (
                  <><CheckCircle className="w-5 h-5 text-blue-600" /> Preview</>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[11px] text-slate-500">Bundles</div>
                  <div className="text-lg font-bold">{result.bundles_parsed}</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[11px] text-slate-500">USNs</div>
                  <div className="text-lg font-bold">{result.usns_parsed}</div>
                </div>
                <div className="bg-slate-50 border rounded p-2" data-testid="stat-running-mtr">
                  <div className="text-[11px] text-slate-500">Running Mtr</div>
                  <div className="text-lg font-bold text-indigo-600">{result.running_mtr_bundles ?? 0}</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[11px] text-slate-500">Errors</div>
                  <div className={`text-lg font-bold ${result.error_count ? 'text-red-600' : 'text-emerald-600'}`}>{result.error_count}</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[11px] text-slate-500">Warnings</div>
                  <div className={`text-lg font-bold ${result.warning_count ? 'text-amber-600' : 'text-slate-400'}`}>{result.warning_count}</div>
                </div>
              </div>

              {applied && (
                <div className="text-emerald-700 font-medium" data-testid="applied-banner">
                  ✓ Audit ID: <code>{result.audit_id}</code> — bundles affected: {result.greige_bundles_affected},
                  storage: {result.greige_storage_inventory_affected}, USNs: {result.greige_usns_affected}
                </div>
              )}

              {result.errors?.length > 0 && (
                <div data-testid="error-list">
                  <div className="font-medium text-red-700 mb-1">Errors (fix and re-upload)</div>
                  <ul className="text-xs text-red-600 space-y-0.5 max-h-40 overflow-y-auto list-disc pl-5">
                    {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </div>
              )}

              {result.warnings?.length > 0 && (
                <div>
                  <div className="font-medium text-amber-700 mb-1">Warnings</div>
                  <ul className="text-xs text-amber-600 space-y-0.5 max-h-40 overflow-y-auto list-disc pl-5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {result.sample?.length > 0 && (
                <div>
                  <div className="font-medium text-slate-700 mb-1">Sample ({result.sample.length} of {result.bundles_parsed})</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 border-b">
                          <th className="py-1 pr-3">Bundle</th>
                          <th className="py-1 pr-3">Design</th>
                          <th className="py-1 pr-3">Shade</th>
                          <th className="py-1 pr-3">Sort No</th>
                          <th className="py-1 pr-3">Type</th>
                          <th className="py-1 pr-3">USNs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.sample.map((s, i) => (
                          <tr key={i} className="border-b border-slate-100" data-testid={`sample-row-${i}`}>
                            <td className="py-1 pr-3 font-mono">{s.bundle_barcode}</td>
                            <td className="py-1 pr-3">{s.design_name}</td>
                            <td className="py-1 pr-3">{s.color_name}</td>
                            <td className="py-1 pr-3">{s.sort_no}</td>
                            <td className="py-1 pr-3">
                              {s.product_type === 'running_mtr' ? (
                                <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                                  Running · {s.meters}m
                                </Badge>
                              ) : (
                                <span className="text-slate-400">Dhoti</span>
                              )}
                            </td>
                            <td className="py-1 pr-3"><Badge variant="secondary">{s.usn_count}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LegacyBundleBackfill;
